import { BooleanFlag, FeatureFlags, VariantFlag } from "./FeatureFlags";
import type { FlagIdentity } from "./flagIdentity";
// Type-only: FliptService.ts imports FLIPT_NAMESPACE from here, so a value import would close a
// runtime require cycle. Types are erased at build time and break the cycle.
import type { FliptService, VariantEvaluation } from "./FliptService";

export const FLIPT_NAMESPACE = "default";

// Widened to the descriptor union: the registry's literal types are all boolean today, which would
// leave the variant predicate below with nothing to narrow from.
const REGISTERED_FLAGS: readonly (BooleanFlag | VariantFlag)[] = Object.values(FeatureFlags);

// Every boolean flag, read straight off the descriptor registry so the flag set lives in exactly one
// place. Defaults, routing, and the dev-flag set below all derive from this, so none can drift from
// the registry.
const BOOLEAN_FLAGS: readonly BooleanFlag[] = REGISTERED_FLAGS.filter((flag): flag is BooleanFlag => flag.type === "boolean");

// Every variant flag, read off the same registry by type so booleans and variants share one source of
// truth. Empty until a variant flag ships in the product registry; variant machinery is exercised by
// test-only descriptors passed straight to the evaluators.
const VARIANT_FLAGS: readonly VariantFlag[] = REGISTERED_FLAGS.filter((flag): flag is VariantFlag => flag.type === "variant");

// Per-flag value served while the Flipt snapshot has not loaded yet, or when Flipt is unreachable.
// Keyed by flag key; a flag defaults on by declaring defaultValue: true on its descriptor.
export const FLIPT_FLAG_DEFAULTS: Readonly<Record<string, boolean>> = Object.fromEntries(BOOLEAN_FLAGS.map((flag) => [flag.key, flag.defaultValue]));

// Per-flag variant served before the snapshot settles or when Flipt is unreachable. Keyed by flag key.
export const VARIANT_FLAG_DEFAULTS: Readonly<Record<string, string>> = Object.fromEntries(VARIANT_FLAGS.map((flag) => [flag.key, flag.defaultVariant]));

export const FLIPT_FLAGS: ReadonlySet<string> = new Set(BOOLEAN_FLAGS.map((flag) => flag.key));

// The split snapshot both consumers read: booleans and variants keyed by flag key, plus the per-variant
// attachment payload (undefined whenever the code default is served — before settle / non-match / down).
export interface FlagValues {
  booleans: Record<string, boolean>;
  variants: Record<string, string>;
  variantAttachments: Record<string, string | undefined>;
}

export function fliptFlagDefault(flag: BooleanFlag): boolean {
  return flag.defaultValue;
}

// The single place flag routing/default resolution lives. Both the imperative singleton and the React
// provider evaluate through here, so the "ask Flipt, fall back to the registered default" rule exists
// once. The identity carries the entityId Flipt buckets on plus the context segment rules target.
export function evaluateFlag(service: FliptService | null, flag: BooleanFlag, identity: FlagIdentity): boolean {
  return service?.evaluate(flag.key, fliptFlagDefault(flag), identity.entityId, identity.context) ?? fliptFlagDefault(flag);
}

// The variant counterpart to evaluateFlag, sharing the same single routing point. Returns the resolved
// key plus its attachment, falling back to the descriptor's default variant (with no attachment) when
// no service is available. The identity mirrors evaluateFlag so both flag kinds bucket the same way.
export function evaluateVariant(service: FliptService | null, flag: VariantFlag, identity: FlagIdentity): VariantEvaluation {
  return service?.evaluateVariant(flag.key, flag.defaultVariant, identity.entityId, identity.context) ?? { variant: flag.defaultVariant };
}

// Evaluate every registered flag in one pass, keyed by flag key. BOOLEAN_FLAGS and VARIANT_FLAGS
// together span the whole registry, so the loops fill the whole split snapshot.
export function evaluateAllFlags(service: FliptService, identity: FlagIdentity): FlagValues {
  const values: FlagValues = { booleans: {}, variants: {}, variantAttachments: {} };
  for (const flag of BOOLEAN_FLAGS) {
    values.booleans[flag.key] = evaluateFlag(service, flag, identity);
  }
  for (const flag of VARIANT_FLAGS) {
    const evaluation = evaluateVariant(service, flag, identity);
    values.variants[flag.key] = evaluation.variant;
    values.variantAttachments[flag.key] = evaluation.attachment;
  }
  return values;
}

// The dev-mode flag set — the boolean-descriptor key set, derived from the registry so the flag keys
// exist in exactly one place. Variant flags serve their default variant in dev and take no override.
export const DEV_FLAGS: readonly string[] = BOOLEAN_FLAGS.map((flag) => flag.key);
